/**
 * Initiative and the activation state machine — display order, the three RAW Tier-1
 * transitions (activate / end turn / end round) as pure named functions over the whole
 * `ActivationState`, the `activated` stamps, and the Tier-2 `set_activation` override that
 * doubles as every Tier-1 command's lossless inverse.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type Result } from "@lancer-vtt/shared";
import { ableUnactedOnSide, freshTurnBudget, isAble, lastActedSide, maxActivations, openingSide, roundComplete } from "../activation";
import type { EncounterCommand } from "../commands";
import { FLYING_STATUS } from "../movement";
import { activeActor, freshActivation, isActor, sideOf, spentMove, type ActivationState, type Combatant, type CombatantId, type EncounterState, type Side, type SitrepProgress } from "../types";
import { changed, requireCombatant, withCombatant, type EncounterHandler } from "./context";

export const applyOrderCombatants: EncounterHandler<"order_combatants"> = (state, cmd) => {
  // The new order must be a permutation of exactly the current combatant ids.
  const currentIds = state.order;
  if (!isPermutation(cmd.order, currentIds)) {
    return err(
      new CommandError(
        `order_combatants: new order must be a permutation of the current ${currentIds.length} combatant id(s)`,
      ),
    );
  }
  const prev = state.order;
  const next: EncounterState = { ...state, order: [...cmd.order] };
  return ok({
    state: next,
    inverse: { type: "order_combatants", actorId: cmd.actorId, order: prev },
    events: changed(null),
  });
};

export const applyActivateUnit: EncounterHandler<"activate_unit"> = (state, cmd) => {
  // Tier-1 gameplay, hard-enforced (start a turn). The legality +
  // effect live in the named {@link activate} transition; the case is thin — capture the
  // prior activation, apply, and inverse via the lossless `set_activation` snapshot.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const prev = state.activation;
  const r = activate(state, cR.value);
  if (!r.ok) return r;
  return ok({
    state: { ...state, activation: r.value },
    inverse: setActivationInverse(cmd.actorId, prev),
    events: changed(null),
  });
};

export const applyEndTurn: EncounterHandler<"end_turn"> = (state, cmd) => {
  // Tier-1 gameplay, hard-enforced (end a turn). Legality + the
  // side flip live in {@link endTurn}; the case is thin.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const prev = state.activation;
  const r = endTurn(state, c);
  if (!r.ok) return r;
  // Fall-trigger 1: a `Flying`
  // unit that spent NONE of its move budget this turn lands at end_turn — Flying is
  // stripped at that instant ("falling" collapsed to status loss in 2D; no
  // altitude/AP-damage). RAW p.104: a flier that doesn't move on its turn falls. Read
  // off `prev` (the turn being ended), not `r.value` (already flipped to idle/next
  // actor).
  //
  // **Keyed on the SPEND COUNTER (`spentMove`), not on the old
  // `budget.move === base.speed` remaining-budget proxy** — that proxy was a live
  // defect, and the comment that used to sit here asserted the opposite of what the
  // code did. Boost ADDS `base.speed` to `budget.move` (`handlers/budget.ts`), so
  // after a Boost the equality is false and a Boost-only, never-moved flier wrongly
  // stayed aloft. The counter only ever counts move actually SPENT, so:
  //   - Boost then no movement → 0 → **GROUNDS** (Boost grants move, never spends it);
  //   - any real movement, even one space → `move_token` records the path cost → aloft;
  //   - `stand_up` → records the whole zeroed pool → does NOT ground (unchanged);
  //   - `teleport_token` → records a flat 1 → does NOT ground (unchanged).
  // Pinned as a paired opposite in `flightFall.test.ts` — an assertion that only
  // checks the grounding half passes against a predicate hardcoded to `true`.
  const flying = c.statuses.find((s) => s.name === FLYING_STATUS);
  const movedNothing = prev.turn.phase === "acting" && spentMove(prev.turn.budget) === 0;
  const grounds = flying !== undefined && movedNothing;
  const nextState = grounds
    ? withCombatant(
        { ...state, activation: r.value },
        { ...c, statuses: c.statuses.filter((s) => s.name !== FLYING_STATUS) },
      )
    : { ...state, activation: r.value };
  const inverse: EncounterCommand = grounds
    ? {
        type: "batch",
        commands: [
          setActivationInverse(cmd.actorId, prev),
          {
            type: "apply_status",
            actorId: cmd.actorId,
            combatantId: c.id,
            status: FLYING_STATUS,
            expiry: flying!.expiry,
            ...(flying!.appliedTurn !== undefined ? { appliedTurn: flying!.appliedTurn } : {}),
            ...(flying!.partner !== undefined ? { partner: flying!.partner } : {}),
            ...(flying!.source !== undefined ? { source: flying!.source } : {}),
          },
        ],
      }
    : setActivationInverse(cmd.actorId, prev);
  return ok({
    state: nextState,
    inverse,
    events: changed(grounds ? c.id : null),
  });
};

export const applyStampActivated: EncounterHandler<"stamp_activated"> = (state, cmd) => {
  // Stamp a combatant into `activated` directly, idempotently —
  // see the command's own doc for the double-activation guard this closes. A no-op (already
  // present) inverts to itself; a genuine stamp inverts to `unstamp_activated`.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const already = state.activation.activated.includes(cmd.combatantId);
  if (already) {
    return ok({
      state,
      inverse: { type: "stamp_activated", actorId: cmd.actorId, combatantId: cmd.combatantId },
      events: changed(null),
    });
  }
  const next: EncounterState = {
    ...state,
    activation: { ...state.activation, activated: [...state.activation.activated, cmd.combatantId] },
  };
  return ok({
    state: next,
    inverse: { type: "unstamp_activated", actorId: cmd.actorId, combatantId: cmd.combatantId },
    events: changed(null),
  });
};

export const applyUnstampActivated: EncounterHandler<"unstamp_activated"> = (state, cmd) => {
  // Internal inverse of a genuine `stamp_activated`; a plain filter (safe/idempotent even on
  // an absent id). Its own inverse is `stamp_activated` back onto the same id.
  const next: EncounterState = {
    ...state,
    activation: { ...state.activation, activated: state.activation.activated.filter((id) => id !== cmd.combatantId) },
  };
  return ok({
    state: next,
    inverse: { type: "stamp_activated", actorId: cmd.actorId, combatantId: cmd.combatantId },
    events: changed(null),
  });
};

export const applyEndRound: EncounterHandler<"end_round"> = (state, cmd) => {
  // Tier-1 gameplay, hard-enforced (end the round). Legality + the
  // side open / round bump / non-actor reaction refresh live in {@link endRound}; the case is thin.
  const prev = state.activation;
  const prevSitrepProgress = state.sitrepProgress;
  const r = endRound(state);
  if (!r.ok) return r;
  // Control's per-round score (roll-free-apply): `cmd.controlDelta` arrives PRE-COMPUTED by the issuer
  // (`@lancer-vtt/rules`'s `controlScoreDelta`, run over the pre-end-of-round state).
  // Applied VERBATIM — no `controlOf`/rules call here — and only when there's a live
  // `sitrepProgress` to accumulate onto (Control-seeded) AND the issuer sent a delta; a
  // no-sitrep or non-Control `end_round` (no `sitrepProgress`, no `controlDelta`) is
  // completely unchanged.
  const nextSitrepProgress = applyControlDelta(prevSitrepProgress, cmd.controlDelta);
  return ok({
    state: { ...state, activation: r.value, sitrepProgress: nextSitrepProgress },
    inverse: setActivationInverse(cmd.actorId, prev, nextSitrepProgress !== prevSitrepProgress ? prevSitrepProgress : undefined),
    events: changed(null),
  });
};

export const applySetActivation: EncounterHandler<"set_activation"> = (state, cmd) => {
  // Tier-2 override AND the lossless inverse of every Tier-1 command.
  // Set the WHOLE `ActivationState` (round + activeSide + activated + reactions +
  // the discriminated turn incl. the in-band budget) wholesale, bypassing legality. Its
  // own inverse is another set_activation back to the pre-apply value, so undo/redo
  // round-trips exactly.
  const prev = state.activation;
  const prevSitrepProgress = state.sitrepProgress;
  // `sitrepProgress` restores wholesale ONLY when the command carries it — an
  // `end_round`-sourced (or another sitrepProgress-carrying) `set_activation` inverse
  // sets this field; every other Tier-1 inverse (activate/end-turn/…) leaves it
  // `undefined`, so `sitrepProgress` stays untouched for those.
  const nextSitrepProgress = cmd.sitrepProgress !== undefined ? cmd.sitrepProgress : state.sitrepProgress;
  return ok({
    state: { ...state, activation: cmd.activation, sitrepProgress: nextSitrepProgress },
    inverse: setActivationInverse(cmd.actorId, prev, cmd.sitrepProgress !== undefined ? prevSitrepProgress : undefined),
    events: changed(null),
  });
};

// ── activation transitions + helpers ───────────────────────────────────────────────
//
// The three RAW Tier-1 transitions, as pure named functions over the
// whole `ActivationState` (the reducer cases stay thin). Each returns the next activation or
// a logged-no-op `err`; the case applies it and builds the lossless `set_activation` inverse.

/**
 * **activate(X)** — start X's turn. Legal only when nobody is mid-turn (`turn.phase ===
 * "idle"`) ∧ X is alive ∧ un-acted ∧ on the active side. Off-side activation is rejected —
 * cross-side fiat is recoverable only via `set_activation` (Tier 2). On success, `turn`
 * becomes `acting` with X bound and its per-turn action/move budget refilled (actions to the
 * allotment, move to X's Speed), AND every combatant's `reactionSlot` is reopened (RAW Core
 * pp. 61, 73: one reaction per turn, "your turn or other characters' turns" — so a new turn,
 * anyone's, is a new reaction window; `decisions/0056`). X's OWN `reactionUses` entry is cleared
 * too (Core p.73: "your uses of it only refresh when your next turn begins"; each activation of
 * an ELITE/ULTRA is a turn) — a unit that Overwatched on one enemy's turn may Brace on the next,
 * but not Overwatch again until its own next turn. Does NOT flip the side or touch `activated`.
 *
 * **The LAPSE** (RAW Core pp. 71-72 — "if the trigger does
 * not occur before your next turn, the readied action is lost"): X's OWN leftover
 * `heldActions[X]` entry, if any, is cleared here too, beside the reaction-slot refresh —
 * an untriggered hold survives every OTHER combatant's turns in between (it is a top-level
 * `ActivationState` sibling precisely so it can), but X's own next activation is exactly the
 * "next turn" boundary RAW lapses it at. Nothing is refunded/spent beyond what `prepare`
 * already charged (the Quick point + ledger stamp) — a Loading weapon prepared-but-never-
 * released stays loaded (the consume-at-release ruling; pinned with a live test).
 */
function activate(state: EncounterState, c: Combatant): Result<ActivationState, CommandError> {
  const a = state.activation;
  if (a.turn.phase !== "idle") {
    return err(new CommandError(
      `activate_unit: ${activeActor(a)} is mid-turn; end its turn before activating ${c.id}`,
    ));
  }
  // An object/deployable is not an activation participant (RAW: objects don't take turns)
  // — reject before the
  // `isAble`/side/activated checks below, which don't know about non-actor kinds.
  if (!isActor(c)) {
    return err(new CommandError(`activate_unit: ${c.kind} ${c.id} is not an actor and cannot be activated`));
  }
  if (!isAble(c)) {
    return err(new CommandError(`activate_unit: combatant ${c.id} is not able to act (destroyed or 0 HP)`));
  }
  if (a.activated.includes(c.id)) {
    return err(new CommandError(`activate_unit: combatant ${c.id} has already acted this round`));
  }
  if (sideOf(c) !== a.activeSide) {
    return err(new CommandError(
      `activate_unit: combatant ${c.id} is on the ${sideOf(c)} side but it is the ${a.activeSide} side's turn`,
    ));
  }
  // The budget is minted fresh on every activation (`freshTurnBudget`): `actionUses`,
  // `protocolUses` and `spent` all reset, so a multi-activation combatant's second activation
  // in a round reopens a fresh turn-virgin window even after its first spent its whole budget.
  const budget = freshTurnBudget(c);
  let heldActions = a.heldActions;
  if (heldActions[c.id] !== undefined) {
    const { [c.id]: _lapsed, ...rest } = heldActions;
    heldActions = rest;
  }
  let reactionUses = a.reactionUses;
  if (reactionUses[c.id] !== undefined) {
    const { [c.id]: _refreshed, ...rest } = reactionUses;
    reactionUses = rest;
  }
  // Bump the monotonic turn-identity counter —
  // every `activate()` call, including a multi-activation combatant's 2nd/3rd turn in one
  // round, gets a strictly fresh ordinal. This is the sole place `turnOrdinal` advances.
  return ok({
    ...a,
    // Every turn start reopens EVERY combatant's slot (RAW: one reaction per turn, anyone's turn).
    reactionSlot: {},
    reactionUses,
    heldActions,
    turn: { phase: "acting", actor: c.id, budget },
    turnOrdinal: a.turnOrdinal + 1,
  });
}

/**
 * **endTurn(X)** — end X's turn. Legal only when X is exactly the unit mid-turn. Returns
 * `turn` to `idle` and flips `activeSide` to the other side — unless that side has no able
 * un-acted units left (the uneven-side consecutive case), then it stays. Both reaction
 * ledgers are untouched: `reactionSlot` survives the idle gap and is only reopened by the next
 * `activate`, anyone's; a combatant's `reactionUses` entry is cleared by its own next `activate`.
 *
 * **Gated entry into `activated`** — `X` is
 * added to `activated` (the dedup set meaning "has no activations left this round") only
 * when its round-scoped tally, `activationsUsed[X]` (incremented here first), reaches
 * {@link maxActivations}`(state, X)`. A partial ELITE/ULTRA — one whose tally is still
 * below its max — stays OUT of `activated` this call, so it stays in `unactedOnSide`,
 * `ableUnactedOnSide` still counts it, `roundComplete` stays false, `perUnitState` renders
 * it `ready`/`on_deck` (never a special "partial" state), and `activate_unit` re-accepts it
 * later on its own side's next turn — all of that for free, because nothing here special-
 * cases the partial case beyond withholding the `activated` write. Every combatant with no
 * multi-activation bonus has `maxActivations === 1`, so its very first `endTurn` always
 * clears the gate — so the common case is an unconditional add.
 */
function endTurn(state: EncounterState, c: Combatant): Result<ActivationState, CommandError> {
  const a = state.activation;
  if (a.turn.phase !== "acting" || a.turn.actor !== c.id) {
    return err(new CommandError(
      a.turn.phase !== "acting"
        ? `end_turn: no turn is in progress (nobody is activating)`
        : `end_turn: combatant ${c.id} is not the unit mid-turn (${a.turn.actor} is)`,
    ));
  }
  const usedCount = (a.activationsUsed[c.id] ?? 0) + 1;
  const activationsUsed = { ...a.activationsUsed, [c.id]: usedCount };
  const fullyDone = usedCount >= maxActivations(state, c);
  const activated =
    fullyDone && !a.activated.includes(c.id) ? [...a.activated, c.id] : a.activated;
  // Compute the flip against the post-end state (the just-ended unit excluded from
  // "remaining" only once it's fully done and lands in `activated` above — a still-partial
  // multi-activation unit is deliberately left OUT of `ended.activated`, so it remains in
  // its OWN side's able-unacted pool too; that's exactly what re-admits it to
  // `activate_unit` on a later turn without any bespoke re-admission logic).
  // A watcher's decline against X lasts the rest of X's turn (`MoveReactionDecline`), so X's
  // own end drops them — every other mover's declines stand.
  const ended: ActivationState = { ...dropSplitMoveOf(dropDeclinesAgainst(a, c.id), c.id), activated, activationsUsed, turn: { phase: "idle" } };
  const thisSide = sideOf(c);
  const otherSide: Side = thisSide === "pc" ? "npc" : "pc";
  const otherHasAble = ableUnactedOnSide({ ...state, activation: ended }, otherSide).length > 0;
  const activeSide: Side = otherHasAble ? otherSide : thisSide;
  return ok({ ...ended, activeSide });
}

/**
 * `a` minus every {@link ActivationState.moveReactionDeclines} entry whose mover is `moverId`.
 * The field is dropped outright when nothing is left, so an encounter that never recorded a
 * decline keeps the exact activation shape it had.
 */
function dropDeclinesAgainst(a: ActivationState, moverId: CombatantId): ActivationState {
  if (a.moveReactionDeclines === undefined) return a;
  const { moveReactionDeclines, ...rest } = a;
  const kept = moveReactionDeclines.filter((d) => d.moverId !== moverId);
  return kept.length > 0 ? { ...rest, moveReactionDeclines: kept } : rest;
}

/** `a` minus its {@link ActivationState.splitMove} when `moverId` is the held mover: a move still held at its turn's end never finishes. */
function dropSplitMoveOf(a: ActivationState, moverId: CombatantId): ActivationState {
  if (a.splitMove?.moverId !== moverId) return a;
  const { splitMove: _dropped, ...rest } = a;
  return rest;
}

/**
 * **endRound** — close the round. Legal only when nobody is mid-turn (`turn.phase ===
 * "idle"`) ∧ the round is complete (no able un-acted units). Clears `activated`, carries every
 * actor's `reactionUses` forward (its refresh is its own next `activate`) while dropping every
 * non-actor's ({@link actorReactionUses}), bumps `round`,
 * and opens the new round on the side that did NOT act last ({@link openingSide}).
 * Deliberately does **NOT** touch `reactionSlot` — the slot is a per-turn window reopened by the
 * next `activate`, never by the round boundary, so a reaction taken in the idle gap before
 * `end_round` stays spent until the next turn starts. A GM cutting a round short uses the
 * `set_activation` override (Tier 2).
 *
 * **Held actions carry forward too** — like `reactionSlot`,
 * {@link ActivationState.heldActions} is NOT a round-boundary concern: RAW lapses an
 * untriggered hold at the PREPARER'S OWN next turn, not at `end_round` (which may complete
 * without that combatant ever activating again this round on the other side). `freshActivation`
 * would otherwise reset it to `{}` (it mints a from-scratch state), so it's spliced back in
 * from the pre-apply activation, exactly like `slot` is passed in explicitly above.
 */
function endRound(state: EncounterState): Result<ActivationState, CommandError> {
  const a = state.activation;
  if (a.turn.phase !== "idle") {
    return err(new CommandError(`end_round: ${activeActor(a)} is mid-turn; end its turn first`));
  }
  if (!roundComplete(state)) {
    return err(new CommandError(`end_round: able un-acted units remain this round`));
  }
  const opening = openingSide(lastActedSide(state));
  // `turnOrdinal` is NOT a round-boundary concern — splice the prior value
  // back in exactly like `heldActions`, since `freshActivation` would otherwise reset it to 0.
  // `moveReactionDeclines` and `splitMove` deliberately are NOT spliced: the round boundary is their backstop clear.
  return ok({
    ...freshActivation(opening, a.round + 1, { slot: a.reactionSlot, uses: actorReactionUses(state) }),
    heldActions: a.heldActions,
    turnOrdinal: a.turnOrdinal,
  });
}

/**
 * The `reactionUses` entries `endRound` carries forward: every actor's, since only the actor's own
 * next `activate` refreshes it. A non-actor (object, deployable) never takes a turn, so its entry
 * is dropped here — the round boundary is its only refresh.
 */
function actorReactionUses(state: EncounterState): ActivationState["reactionUses"] {
  const kept: ActivationState["reactionUses"] = {};
  for (const [id, uses] of Object.entries(state.activation.reactionUses)) {
    const c = state.combatants[id];
    if (c !== undefined && isActor(c)) kept[id] = uses;
  }
  return kept;
}

/**
 * Build the lossless `set_activation` inverse of a Tier-1 gameplay command (or of
 * `set_activation` itself) — restore the captured prior whole {@link ActivationState}. The
 * single shape every activation case's inverse uses — `set_activation` is dual-use override
 * + inverse, and the whole union travels in one field, so the inverse is a single snapshot.
 *
 * `sitrepProgress` is optional and additive: pass it ONLY when
 * the command being inverted itself changed `sitrepProgress` (today, only `end_round`'s
 * embedded `controlDelta`) — every other call site omits it, so those inverses leave
 * `sitrepProgress` untouched on apply.
 */
function setActivationInverse(actorId: string, activation: ActivationState, sitrepProgress?: SitrepProgress): EncounterCommand {
  return { type: "set_activation", actorId, activation, sitrepProgress };
}

/**
 * Apply Control's embedded per-round score delta VERBATIM (roll-free-apply):
 * `progress.scores[side] += delta[side]` for each side, no `controlOf`/rules call.
 * Returns `progress` UNCHANGED
 * (same reference) when either is absent — no live `sitrepProgress` (not Control-seeded)
 * or the issuer sent no delta (a non-Control `end_round`) — so the reference-equality
 * check callers use to detect "did this apply anything" (`nextSitrepProgress !==
 * prevSitrepProgress`) stays correct.
 */
function applyControlDelta(
  progress: SitrepProgress | undefined,
  delta: Record<Side, number> | undefined,
): SitrepProgress | undefined {
  if (!progress || !delta) return progress;
  return { scores: { pc: progress.scores.pc + delta.pc, npc: progress.scores.npc + delta.npc } };
}

// ── permutation check ──────────────────────────────────────────────────────────

/** True iff `a` and `b` contain exactly the same ids (same multiset, no extras/missing). */
function isPermutation(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const counts = new Map<string, number>();
  for (const id of b) counts.set(id, (counts.get(id) ?? 0) + 1);
  for (const id of a) {
    const n = counts.get(id);
    if (n === undefined || n === 0) return false;
    counts.set(id, n - 1);
  }
  return true;
}
